// BUILD-042 employee PROFILE CORRECTION REQUESTS (Oct 19 appendix).
// Authority: docs/superpowers/decisions/2026-10-07-build-042-owner-led-onboarding-documents-and-test-roster.md (Oct 19 appendix)
//
// This is the REAL, durable correction-request surface. A correction request is a persisted ask
// for HR to correct a GOVERNED fact the employee can see but cannot edit (legal name, job title,
// department, manager, hire date, employment status). It is NOT a generalized approvals engine:
// it supports exactly one shape — a field-level correction with a server-derived current value,
// a proposed value, a reason, and a single terminal HR decision (resolve / reject).
//
// Everything here is backed by the HR Lambda:
//   GET  /hr/me/correction-requests          — the caller's own requests + the fields they may request
//   POST /hr/me/correction-requests          — submit about the caller's OWN profile
//   GET  /hr/admin/correction-requests        — the HR reviewer queue (requires hr.correction.review)
//   POST /hr/admin/correction-requests/:id/resolve|reject — one terminal decision, note required
//
// Server-enforced floors this context relies on (never re-decided client-side):
//   * own-profile only — the submit route always uses the caller as the subject;
//   * one OPEN request per field — a duplicate submit returns a clean 409;
//   * no-self-decision — a reviewer can never decide a request they submitted (403);
//   * resolving/rejecting NEVER writes the profile or sign-in — HR makes any real change through the
//     ordinary edit screens; the decision only records the outcome + a note;
//   * the submission snapshot is immutable and exactly one decision is allowed (migration 204 guard).
//
// The employee's own submitted request is deliberately NOT part of their My HR action count
// (that count is server-scoped, GET /hr/me/attention) — a submitted correction is "Awaiting HR
// review — no action needed from you", so it must not read as the employee's to-do.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  getMyCorrectionRequests, submitMyCorrectionRequest, getCorrectionRequestsForReview,
  resolveCorrectionRequest, rejectCorrectionRequest,
  type CorrectionRequestRecord, type CorrectionRequestStatus, type CorrectionFieldOption,
} from '../../api/client';

export type { CorrectionRequestStatus, CorrectionFieldOption } from '../../api/client';
export type CorrectionRequest = CorrectionRequestRecord;

/** A request is "open" while it waits for HR; resolved/rejected are terminal. These helpers keep the
 *  status vocabulary in one place so the UI never hard-codes the server tokens. */
export function isOpen(r: Pick<CorrectionRequest, 'status'>): boolean { return r.status === 'open'; }
export function isTerminal(r: Pick<CorrectionRequest, 'status'>): boolean { return r.status === 'resolved' || r.status === 'rejected'; }

export interface CorrectionDecisionResult { ok: boolean; status?: number; code?: string; message?: string }

interface HrRequestsContextValue {
  /** The caller's OWN requests (full history), loaded from the server. */
  myRequests: CorrectionRequest[];
  /** The fixed allow-list of governed fields the caller may request a correction for (server-defined). */
  correctableFields: CorrectionFieldOption[];
  /** The HR reviewer queue — empty unless the caller holds hr.correction.review. */
  reviewRequests: CorrectionRequest[];
  loadingMine: boolean;
  loadingReview: boolean;
  /** True once the caller is known NOT to hold the reviewer capability (the admin list returned 404). */
  reviewUnavailable: boolean;

  /** Submit a correction about the caller's OWN profile. Returns a structured result so the caller can
   *  distinguish a clean 409 (an open request for that field already exists) from other failures. */
  submitRequest: (input: { fieldKey: string; proposedValue: string; reason: string }) => Promise<CorrectionDecisionResult>;
  /** Resolve (correction handled) — a note is required by the server. */
  resolveRequest: (requestId: string, note: string) => Promise<CorrectionDecisionResult>;
  /** Reject (declined) — an explanation for the employee is required by the server. */
  rejectRequest: (requestId: string, note: string) => Promise<CorrectionDecisionResult>;
  /** Client-side mirror of the server no-self-decision floor, used only to disable the control and
   *  explain why; the server re-enforces it regardless. */
  canDecide: (request: Pick<CorrectionRequest, 'status' | 'employeeId' | 'requestedByEmployeeId'>, viewerEmployeeId: string) => boolean;

  refreshMine: () => void;
  refreshReview: () => void;
}

const HrRequestsContext = createContext<HrRequestsContextValue | null>(null);

function toResult(e: any): CorrectionDecisionResult {
  return { ok: false, status: typeof e?.status === 'number' ? e.status : undefined, code: typeof e?.code === 'string' ? e.code : undefined, message: e?.message };
}

export function HrRequestsProvider({ children }: { children: ReactNode }) {
  const [myRequests, setMyRequests] = useState<CorrectionRequest[]>([]);
  const [correctableFields, setCorrectableFields] = useState<CorrectionFieldOption[]>([]);
  const [reviewRequests, setReviewRequests] = useState<CorrectionRequest[]>([]);
  const [loadingMine, setLoadingMine] = useState(false);
  const [loadingReview, setLoadingReview] = useState(false);
  const [reviewUnavailable, setReviewUnavailable] = useState(false);
  // Guards so a stale response from an earlier fetch can never overwrite a newer one.
  const mineSeq = useRef(0);
  const reviewSeq = useRef(0);

  const refreshMine = useCallback(() => {
    const seq = ++mineSeq.current;
    setLoadingMine(true);
    getMyCorrectionRequests()
      .then((r) => { if (seq !== mineSeq.current) return; setMyRequests(r.requests); setCorrectableFields(r.fields); })
      .catch(() => { /* keep the last good list; the picker/history simply shows nothing new */ })
      .finally(() => { if (seq === mineSeq.current) setLoadingMine(false); });
  }, []);

  const refreshReview = useCallback(() => {
    const seq = ++reviewSeq.current;
    setLoadingReview(true);
    // 'all' so the reviewer queue can show history (resolved/rejected) alongside open requests.
    getCorrectionRequestsForReview({ status: 'all' })
      .then((r) => { if (seq !== reviewSeq.current) return; setReviewRequests(r.requests); setReviewUnavailable(false); })
      .catch((e: any) => {
        if (seq !== reviewSeq.current) return;
        // 404 = the caller holds no reviewer capability; that is an expected "surface not present", not an error.
        if (e?.status === 404) { setReviewRequests([]); setReviewUnavailable(true); }
      })
      .finally(() => { if (seq === reviewSeq.current) setLoadingReview(false); });
  }, []);

  useEffect(() => { refreshMine(); refreshReview(); }, [refreshMine, refreshReview]);

  const submitRequest = useCallback(async (input: { fieldKey: string; proposedValue: string; reason: string }): Promise<CorrectionDecisionResult> => {
    try {
      await submitMyCorrectionRequest(input);
      refreshMine();
      return { ok: true };
    } catch (e: any) { return toResult(e); }
  }, [refreshMine]);

  const decide = useCallback(async (fn: () => Promise<unknown>): Promise<CorrectionDecisionResult> => {
    try { await fn(); refreshReview(); refreshMine(); return { ok: true }; }
    catch (e: any) { return toResult(e); }
  }, [refreshReview, refreshMine]);

  const resolveRequest = useCallback((requestId: string, note: string) => decide(() => resolveCorrectionRequest(requestId, note)), [decide]);
  const rejectRequest = useCallback((requestId: string, note: string) => decide(() => rejectCorrectionRequest(requestId, note)), [decide]);

  const canDecide = useCallback((request: Pick<CorrectionRequest, 'status' | 'employeeId' | 'requestedByEmployeeId'>, viewerEmployeeId: string): boolean => {
    return request.status === 'open' && request.employeeId !== viewerEmployeeId && request.requestedByEmployeeId !== viewerEmployeeId;
  }, []);

  return (
    <HrRequestsContext.Provider
      value={{
        myRequests, correctableFields, reviewRequests, loadingMine, loadingReview, reviewUnavailable,
        submitRequest, resolveRequest, rejectRequest, canDecide, refreshMine, refreshReview,
      }}
    >
      {children}
    </HrRequestsContext.Provider>
  );
}

export function useHrRequests(): HrRequestsContextValue {
  const ctx = useContext(HrRequestsContext);
  if (!ctx) throw new Error('useHrRequests must be used within an HrRequestsProvider (see App.tsx)');
  return ctx;
}
